import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loading, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { fmtDate, fmtTime, daysUntil } from "@/lib/helpers";
import {
  Users, BookOpen, CalendarDays, Award, TrendingUp, ShieldCheck, GraduationCap,
  Clock, AlertTriangle, CheckCircle2, ArrowRight, Presentation, UserCheck,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [compliance, setCompliance] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, sess] = await Promise.all([api.get("/stats/dashboard"), api.get("/sessions")]);
      setStats(s.data);
      setSessions(sess.data.filter((x) => new Date(x.start) > new Date()).slice(0, 5));
      if (user.role !== "trainer") {
        const c = await api.get(user.role === "admin" ? "/compliance" : "/compliance/me");
        setCompliance(c.data);
      }
    })();
  }, [user.role]);

  if (!stats) return <Loading />;

  const greeting = `${new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening"}, ${user.name.split(" ")[0]}`;

  return (
    <div>
      <PageHeader title={greeting} subtitle={user.role === "admin" ? "System-wide training & compliance overview." : user.role === "trainer" ? "Your sessions and learner progress." : "Your learning journey at a glance."} testid="dashboard-header" />

      {user.role === "admin" && <AdminDash stats={stats} compliance={compliance} sessions={sessions} navigate={navigate} />}
      {user.role === "trainer" && <TrainerDash stats={stats} sessions={sessions} navigate={navigate} />}
      {user.role === "employee" && <EmployeeDash stats={stats} compliance={compliance} sessions={sessions} navigate={navigate} />}
    </div>
  );
}

function UpcomingSessions({ sessions, navigate }) {
  return (
    <Card className="p-5 lg:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold">Upcoming Sessions</h3>
        <Button variant="ghost" size="sm" onClick={() => navigate("/sessions")} data-testid="view-all-sessions-button">
          View all <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      <div className="space-y-2">
        {sessions.length === 0 && <p className="text-sm text-muted-foreground py-4">No upcoming sessions.</p>}
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-accent/30 transition-colors">
            <div className="h-11 w-11 rounded-lg bg-primary/10 flex flex-col items-center justify-center shrink-0">
              <span className="text-[10px] uppercase text-primary font-semibold">{fmtDate(s.start, { month: "short" })}</span>
              <span className="font-heading font-bold text-primary leading-none">{new Date(s.start).getDate()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{s.course?.title}</p>
              <p className="text-xs text-muted-foreground">{fmtTime(s.start)} · {s.location} · {s.trainer_name}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs font-medium">{s.enrolled_count}/{s.capacity}</p>
              <p className="text-[11px] text-muted-foreground">seats</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AdminDash({ stats, compliance, sessions, navigate }) {
  const c = stats.compliance;
  const pieData = [
    { name: "Compliant", value: c.compliant, color: "#10B981" },
    { name: "Expiring", value: c.expiring, color: "#F59E0B" },
    { name: "Overdue", value: c.overdue, color: "#EF4444" },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={stats.total_users} icon={Users} testid="stat-users" />
        <StatCard label="Courses" value={stats.total_courses} icon={BookOpen} testid="stat-courses" />
        <StatCard label="Sessions" value={stats.total_sessions} icon={CalendarDays} testid="stat-sessions" />
        <StatCard label="Certificates" value={stats.certificates} icon={Award} accent="text-emerald-600" testid="stat-certificates" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Compliance Health</h3>
          <p className="text-xs text-muted-foreground mb-4">Across {c.total} mandatory assignments</p>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={48} outerRadius={72} paddingAngle={3}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-8 text-center">No compliance data yet.</p>}
          <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/compliance")} data-testid="goto-compliance-button">
            Manage compliance <ShieldCheck className="h-4 w-4 ml-1.5" />
          </Button>
        </Card>
        <UpcomingSessions sessions={sessions} navigate={navigate} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-5 border-l-4 border-l-red-500">
          <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /><span className="text-sm font-medium">Overdue</span></div>
          <p className="font-heading text-3xl font-bold mt-2">{c.overdue}</p>
          <p className="text-xs text-muted-foreground">compliance items need attention</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-2 text-amber-600"><Clock className="h-5 w-5" /><span className="text-sm font-medium">Expiring Soon</span></div>
          <p className="font-heading text-3xl font-bold mt-2">{c.expiring}</p>
          <p className="text-xs text-muted-foreground">within the 90-day horizon</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 text-emerald-600"><TrendingUp className="h-5 w-5" /><span className="text-sm font-medium">Completion Rate</span></div>
          <p className="font-heading text-3xl font-bold mt-2">{stats.completion_rate}%</p>
          <p className="text-xs text-muted-foreground">of all enrollments completed</p>
        </Card>
      </div>
    </div>
  );
}

function TrainerDash({ stats, sessions, navigate }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="My Sessions" value={stats.my_sessions} icon={CalendarDays} testid="stat-my-sessions" />
        <StatCard label="Learners" value={stats.learners} icon={Users} testid="stat-learners" />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} accent="text-emerald-600" testid="stat-completed" />
        <StatCard label="Courses" value={stats.courses} icon={BookOpen} testid="stat-trainer-courses" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <UpcomingSessions sessions={sessions} navigate={navigate} />
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/sessions")} data-testid="action-schedule-session"><CalendarDays className="h-4 w-4 mr-2" />Schedule a session</Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/attendance")} data-testid="action-mark-attendance"><UserCheck className="h-4 w-4 mr-2" />Mark attendance</Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/courses")} data-testid="action-create-course"><Presentation className="h-4 w-4 mr-2" />Create a course</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function EmployeeDash({ stats, compliance, sessions, navigate }) {
  const pending = compliance.filter((c) => c.computed_status !== "compliant");
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Enrollments" value={stats.my_enrollments} icon={GraduationCap} testid="stat-my-enrollments" />
        <StatCard label="Completed" value={stats.my_completed} icon={CheckCircle2} accent="text-emerald-600" testid="stat-my-completed" />
        <StatCard label="Certificates" value={stats.my_certificates} icon={Award} accent="text-emerald-600" testid="stat-my-certs" />
        <StatCard label="Training Hours" value={stats.training_hours} icon={Clock} sub="completed" testid="stat-hours" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Mandatory Training</h3>
            <StatusBadge status={pending.length ? "overdue" : "compliant"}>{pending.length ? `${pending.length} pending` : "All clear"}</StatusBadge>
          </div>
          <div className="space-y-2">
            {pending.length === 0 && <p className="text-sm text-muted-foreground py-4">You're fully compliant. 🎉</p>}
            {pending.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.course?.title}</p>
                  <p className="text-xs text-muted-foreground">Due {fmtDate(c.due_date)}{c.days_left != null && ` · ${c.days_left < 0 ? Math.abs(c.days_left) + " days overdue" : c.days_left + " days left"}`}</p>
                </div>
                <StatusBadge status={c.computed_status} />
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => navigate("/compliance")} data-testid="view-compliance-button">View all compliance</Button>
        </Card>
        <UpcomingSessions sessions={sessions} navigate={navigate} />
      </div>
    </div>
  );
}
